import {
  OBAvailableEndpoints,
  OBAvailableVirtualSensors,
  OBCloudSensors,
  OBContact,
  OBContacts,
  OBEndpointConfig,
  OBHardwareInfo,
  OBIdToContactMap,
  OBIdToScannerMap,
  OBIdToSensorMap,
  OBIdToSiteMap,
  OBInternetExposure,
  OBLogSource,
  OBLogSourceMFA,
  OBProducts,
  OBScanner,
  OBScanners,
  OBSensor,
  OBSensors,
  OBSite,
  OBSites,
  OBVirtualizationSoftware,
  OBVirtualScanner,
  OBVirtualSensorConfig,
  RawOBAvailableEndpoints,
  RawOBAvailableVirtualSensors,
  Store,
} from '../../types';
import {
  RawOBCloudSensors,
  RawOBContact,
  RawOBContactSection,
  RawOBEndpointConfig,
  RawOBExternal,
  RawOBLogSource,
  RawOBScanner,
  RawOBSensor,
  RawOBSite,
  RawOBSiteSection,
  RawOBVirtualScanner,
  RawOBVirtualSensorConfig,
} from '../../actions/onboarding';
import { NEWLINE_COMMA_DELIMITER, PREFIX_TO_MASK_MAP } from '../../constants';
import { getPluralOrSingularNoun } from '../../utils';

// transformation between the frontend types and the API schema
// Note primary/secondary attributes are often represented by ordering

const contactToAPI = (id: string, contact: OBContact): RawOBContact => {
  // Do not send empty phone number objects
  const phones = [];
  if (contact.primaryNumber) {
    phones.push({
      number: contact.primaryNumber,
      type: contact.primaryNumberType,
      id: contact.primaryNumberId,
      extension: contact.primaryExtensionNumber,
    });
  }
  if (contact.secondaryNumber) {
    phones.push({
      number: contact.secondaryNumber,
      type: contact.secondaryNumberType,
      id: contact.secondaryNumberId,
      extension: contact.secondaryExtensionNumber,
    });
  }

  return {
    first_name: contact.firstName,
    last_name: contact.lastName,
    escalation: {
      incident: contact.isBasicEscalationContact,
    },
    is_mailing_list: contact.isDL,
    email: contact.email,
    access: {
      is_risk_portal_user: contact.isRiskUser,
      is_portal_user: contact.isPortalUser,
      is_log_search_user: contact.isLogSearchUser,
      is_managed_awareness_user: contact.isManagedAwarenessUser,
      is_activate_vlc_user: contact.isActivateVlcUser,
      is_mycyber_dashboard_user: contact.isMyCyberDashboardUser,
    },
    intelligence_reports: contact.isReportRecipient,
    phone: phones,
    is_admin: contact.contactType !== 'normal', // i.e. primary || secondary
    is_primary_contact: contact.contactType === 'primary',
    is_secondary_contact: contact.contactType === 'secondary',
    is_msp: contact.isMSP,
    is_shipping_contact: contact.isShippingContact,
    is_executive_sponsor: contact.isExecutiveSponsor,
    role: contact.role,
    id,
  };
};

const contactFromAPI = (rawContact: RawOBContact): OBContact => {
  return {
    firstName: rawContact.first_name,
    lastName: rawContact.last_name,
    email: rawContact.email,
    primaryNumber: (rawContact.phone && rawContact.phone[0]?.number) ?? '',
    primaryNumberType:
      (rawContact.phone && rawContact.phone[0]?.type) ?? 'office',
    primaryNumberId: (rawContact.phone && rawContact.phone[0]?.id) ?? '',
    primaryExtensionNumber:
      (rawContact.phone && rawContact.phone[0]?.extension) ?? '',
    secondaryNumber: (rawContact.phone && rawContact.phone[1]?.number) ?? '',
    secondaryNumberType:
      (rawContact.phone && rawContact.phone[1]?.type) ?? 'mobile',
    secondaryNumberId: (rawContact.phone && rawContact.phone[1]?.id) ?? '',
    secondaryExtensionNumber:
      (rawContact.phone && rawContact.phone[1]?.extension) ?? '',
    role: rawContact.role ?? '',
    isDL: rawContact.is_mailing_list,
    isMSP: rawContact.is_msp,
    isShippingContact: rawContact.is_shipping_contact ?? false,
    contactType: rawContact.is_primary_contact
      ? 'primary'
      : rawContact.is_secondary_contact
      ? 'secondary'
      : 'normal',
    isBasicEscalationContact: rawContact.escalation.incident,
    isReportRecipient: rawContact.intelligence_reports,
    isPortalUser: rawContact.access.is_portal_user,
    isLogSearchUser: rawContact.access.is_log_search_user,
    isRiskUser: rawContact.access.is_risk_portal_user,
    isManagedAwarenessUser: rawContact.access.is_managed_awareness_user,
    isActivateVlcUser: rawContact.access.is_activate_vlc_user,
    isExecutiveSponsor: rawContact.is_executive_sponsor,
    isMyCyberDashboardUser: rawContact.access.is_mycyber_dashboard_user,
  };
};

const contactsToAPI = (contacts: OBContacts): RawOBContactSection => {
  // note the bang operator: we're assuming that there must always be a primary contact
  const primaryContactId = Object.keys(contacts.contacts).find(
    (id) => contacts.contacts[id].contactType === 'primary',
  )!;
  const { [primaryContactId]: primaryContact, ...nonPrimaryContacts } =
    contacts.contacts;
  const contactsList = [];
  if (primaryContact) {
    contactsList.push(contactToAPI(primaryContactId, primaryContact));
  }
  contactsList.push(
    ...Object.entries(nonPrimaryContacts).map(([id, contact]) =>
      contactToAPI(id, contact),
    ),
  );
  return {
    contacts: contactsList,
    completed: contacts.completed,
    finalized: contacts.finalized,
  };
};

const contactsFromAPI = (rawContacts: RawOBContactSection): OBContacts => ({
  contacts: rawContacts.contacts.reduce<OBIdToContactMap>(
    (map, rawContact) => ({
      ...map,
      [rawContact.id]: contactFromAPI(rawContact),
    }),
    {},
  ),
  completed: rawContacts.completed,
  finalized: rawContacts.finalized,
  loading: false,
});

const internetExposureToAPI = (external: OBInternetExposure): RawOBExternal => {
  return {
    outbound_allowed: false,
    ssl_disabled: false,
    external_scan_allowed: external.vulScanIPsConfigured,
    external_scan_info: {
      addresses: external.ipAddresses
        .split(NEWLINE_COMMA_DELIMITER)
        .map((item) => item.trim())
        .filter((item) => item !== ''), // Filter out empty strings
      dns: external.dnsDomains
        .split(NEWLINE_COMMA_DELIMITER)
        .map((item) => item.trim())
        .filter((item) => item !== ''), // Filter out empty strings
    },
    domain_ownership_confirmed: external.domainOwnershipConfirmed,
    no_external_hosts: external.noExternalHosts,
    client_vpn: {},
    completed: external.completed,
    finalized: external.finalized,
    invalid_targets: external.invalidTargets,
  };
};

const internetExposureFromAPI = (
  rawExternal: RawOBExternal,
): OBInternetExposure => {
  return {
    ipAddresses: rawExternal.external_scan_info.addresses
      ? rawExternal.external_scan_info.addresses.join('\n')
      : '',
    dnsDomains: rawExternal.external_scan_info.dns
      ? rawExternal.external_scan_info.dns.join('\n')
      : '',
    vulScanIPsConfigured: rawExternal.external_scan_allowed,
    domainOwnershipConfirmed: rawExternal.domain_ownership_confirmed,
    noExternalHosts: rawExternal.no_external_hosts,
    completed: rawExternal.completed,
    finalized: rawExternal.finalized,
    invalidTargets: rawExternal.invalid_targets,
  };
};

const siteLogSourceToAPI = (site: OBSite): RawOBLogSource => {
  const { vpn, mfa, iam, dns, dhcp, antivirus, ids, proxy, wap, cls } = site;
  return {
    iam,
    dns,
    dhcp,
    antivirus,
    ids,
    proxy,
    wap,
    client_vpn: {
      client_vpn: vpn,
      two_factor_auth: mfa as OBLogSourceMFA,
    },
    cls,
  };
};

const siteLogSourceFromAPI = (rawSite: RawOBSite): OBLogSource => {
  const {
    log_sources: {
      client_vpn: { two_factor_auth: mfa, client_vpn: vpn },
      ...rest
    },
  } = rawSite;
  return {
    ...rest,
    mfa,
    vpn: vpn || '',
    iam: rawSite.log_sources.iam || [],
    dns: rawSite.log_sources.dns || [],
    dhcp: rawSite.log_sources.dhcp || [],
    antivirus: rawSite.log_sources.antivirus || [],
    ids: rawSite.log_sources.ids || [],
    wap: rawSite.log_sources.wap || [],
    cls: rawSite.log_sources.cls,
  };
};

const sitesToAPI = (sites: OBSites): RawOBSiteSection => {
  return {
    sites: [
      ...Object.entries(sites.sites).map(([id, site]) => siteToAPI(id, site)),
    ],
    completed: sites.completed,
    finalized: sites.finalized,
    configure_connectwise: sites.configureConnectWise,
  };
};

const siteToAPI = (id: string, site: OBSite): RawOBSite => ({
  id,
  name: site.name,
  address: site.address,
  time_zone: site.timezone,
  firewall_models: site.firewalls,
  active_directory_controllers: site.adc,
  switch_models: site.switches,
  network_segments: site.ipSubnets
    ? site.ipSubnets.split(/,|\n/).map((v) => v.trim())
    : [],
  scanning_blacklist: site.scanningBlacklist
    ? site.scanningBlacklist.split(', ')
    : [],
  monitoring_blacklist: site.networkBlacklist
    ? site.networkBlacklist.split(', ')
    : [],
  security_devices: site.devices ? site.devices.split(', ') : [],
  adc_is_dhcp: site.isADCDHCP,
  adc_is_dns: site.isADCDNS,
  third_party_site: site.thirdPartySite,
  oldest_windows_version: site.oldestWindowsVersion,
  notes: site.notes,
  log_sources: siteLogSourceToAPI(site),
  finalized: site.finalized,
});

const siteFromAPI = (rawSite: RawOBSite): OBSite => ({
  ...siteLogSourceFromAPI(rawSite),
  name: rawSite.name,
  address: rawSite.address,
  timezone: rawSite.time_zone,
  firewalls: rawSite.firewall_models || [],
  switches: rawSite.switch_models || [],
  ipSubnets: rawSite.network_segments
    ? rawSite.network_segments.join(', ')
    : '',
  devices: rawSite.security_devices ? rawSite.security_devices.join(', ') : '',
  networkBlacklist: rawSite.monitoring_blacklist
    ? rawSite.monitoring_blacklist.join(', ')
    : '',
  scanningBlacklist: rawSite.scanning_blacklist
    ? rawSite.scanning_blacklist.join(', ')
    : '',
  adc: rawSite.active_directory_controllers,
  isADCDHCP: rawSite.adc_is_dhcp,
  isADCDNS: rawSite.adc_is_dns,
  thirdPartySite: rawSite.third_party_site,
  oldestWindowsVersion: rawSite.oldest_windows_version,
  notes: rawSite.notes,
  finalized: rawSite.finalized,
});

const sitesFromAPI = (rawSites: RawOBSiteSection): OBSites => {
  return {
    sites: rawSites.sites.reduce<OBIdToSiteMap>(
      (map, rawSite) => ({
        ...map,
        [rawSite.id]: siteFromAPI(rawSite),
      }),
      {},
    ),
    completed: rawSites.completed,
    finalized: rawSites.finalized,
    configureConnectWise: rawSites.configure_connectwise,
  };
};

const sensorsToAPI = (
  sensors: OBSensors,
): { sensors: ReadonlyArray<RawOBSensor>; completed: boolean } => {
  return {
    sensors: [
      ...Object.entries(sensors.sensors).map(([id, sensor]) =>
        sensorToAPI(id, sensor),
      ),
    ],
    completed: sensors.completed,
  };
};

const sensorToAPI = (id: string, sensor: OBSensor): RawOBSensor => ({
  id,
  name: sensor.name,
  location_id: sensor.siteId,
  network_configuration: sensor.isIPAutoConfig
    ? null
    : {
        address: sensor.ipAddress,
        subnet_mask:
          PREFIX_TO_MASK_MAP[
            sensor.subnetMask as keyof typeof PREFIX_TO_MASK_MAP
          ] || sensor.subnetMask,
        gateway: sensor.gateway,
      },
  primary_dns: sensor.primaryDns,
  secondary_dns: sensor.secondaryDns,
  model: sensor.model,
  recommendation_overridden: !!sensor.modelChanged,
  series: sensor.series,
  configuration: sensor.packetConfig,
  shipping_address: sensor.shippingSiteLocation,
  shipping_contact: {
    id: sensor.shippingContactId,
    first_name: sensor.shippingContactFirstName,
    last_name: sensor.shippingContactLastName,
    email: sensor.shippingContactEmail,
    phone: [
      {
        number: sensor.shippingContactPhoneNumber,
        type: sensor.shippingContactPhoneType,
      },
    ],
  },
  shipping_notes: sensor.shippingNotes,
  need_rackmounts: false,
  capabilities: sensor.currentCapabilities,
  configuration_task_id: sensor.configurationTaskID,
});

const sensorsFromAPI = (rawSensors: {
  sensors: ReadonlyArray<RawOBSensor>;
  completed: boolean;
}): OBSensors => {
  return {
    sensors: rawSensors.sensors.reduce<OBIdToSensorMap>(
      (map, rawSensor) => ({
        ...map,
        [rawSensor.id]: {
          name: rawSensor.name,
          model: rawSensor.model,
          series: rawSensor.series,
          siteId: rawSensor.location_id,
          isIPAutoConfig: !rawSensor.network_configuration,
          ipAddress: rawSensor.network_configuration?.address ?? '',
          subnetMask: rawSensor.network_configuration?.subnet_mask ?? '',
          gateway: rawSensor.network_configuration?.gateway ?? '',
          primaryDns: rawSensor.primary_dns,
          secondaryDns: rawSensor.secondary_dns ?? '',
          packetConfig: rawSensor.configuration,
          currentCapabilities: rawSensor.capabilities ?? {},
          recommendationOverridden: rawSensor.recommendation_overridden,
          modelChanged: rawSensor.recommendation_overridden,
          isShippingToNewSite: !rawSensor.shipping_address.id,
          shippingSiteId: rawSensor.shipping_address.id,
          shippingSiteLocation: { ...rawSensor.shipping_address },
          isShippingToNewContact: !rawSensor.shipping_contact.id,
          shippingContactId: rawSensor.shipping_contact.id,
          shippingContactFirstName: rawSensor.shipping_contact.first_name,
          shippingContactLastName: rawSensor.shipping_contact.last_name,
          shippingContactEmail: rawSensor.shipping_contact.email,
          shippingContactPhoneNumber:
            rawSensor.shipping_contact.phone[0].number,
          shippingContactPhoneType: rawSensor.shipping_contact.phone[0].type,
          shippingNotes: rawSensor.shipping_notes,
          configurationTaskID: rawSensor.configuration_task_id,
        },
      }),
      {},
    ),
    completed: rawSensors.completed,
    loading: false,
  };
};

const cloudSensorsToAPI = (config: OBCloudSensors): RawOBCloudSensors => {
  return {
    amazon: {
      regions: config.aws.regions ?? [],
      cloudwatch: config.aws.cloudWatch,
      cloudtrail: config.aws.cloudTrail,
      guardduty: config.aws.guardDuty,
      acl: config.aws.acl,
      eks: config.aws.eks,
      completed: config.aws.completed,
    },
    azure: {
      sentinel: config.azure.sentinel,
      security_center: config.azure.securityCenter,
      log_analytics: config.azure.logAnalytics,
      completed: config.azure.completed,
    },
    office365: {
      licenses: config.o365.licenses ?? [],
      dlp: config.o365.dlp,
      completed: config.o365.completed,
    },
    salesforce: {
      licenses: config.sf.licenses ?? [],
      completed: config.sf.completed,
    },
    geo_login: {
      countries: config.geo.countries ?? [],
      completed: config.geo.completed,
    },
    gcp: {
      licenses: config.gcp.licenses ?? [],
    },
    imanage: {
      licenses: config.imanage.licenses ?? [],
      completed: config.imanage.completed,
    },
    finalized: config.finalized,
  };
};

const cloudSensorsFromAPI = (rawConfig: RawOBCloudSensors): OBCloudSensors => {
  return {
    aws: {
      regions: rawConfig.amazon.regions,
      guardDuty: rawConfig.amazon.guardduty,
      cloudWatch: rawConfig.amazon.cloudwatch,
      cloudTrail: rawConfig.amazon.cloudtrail,
      acl: rawConfig.amazon.acl,
      eks: rawConfig.amazon.eks,
      completed: rawConfig.amazon.completed,
    },
    azure: {
      logAnalytics: rawConfig.azure.log_analytics,
      securityCenter: rawConfig.azure.security_center,
      sentinel: rawConfig.azure.sentinel,
      completed: rawConfig.azure.completed,
    },
    o365: {
      licenses: rawConfig.office365.licenses,
      dlp: rawConfig.office365.dlp,
      completed: rawConfig.office365.completed,
    },
    sf: {
      licenses: rawConfig.salesforce.licenses,
      completed: rawConfig.salesforce.completed,
    },
    geo: {
      countries: rawConfig.geo_login.countries,
      completed: rawConfig.geo_login.completed,
    },
    gcp: {
      licenses: rawConfig.gcp?.licenses, // gcp is not required in the api spec
    },
    imanage: {
      licenses: rawConfig.imanage?.licenses,
      completed: rawConfig.imanage?.completed,
    },
    finalized: rawConfig.finalized,
  };
};

const scannerToAPI = (id: string, scanner: OBScanner): RawOBScanner => ({
  id,
  name: scanner.name,
  location_id: scanner.siteId,
  network_configuration: scanner.isIPAutoConfig
    ? null
    : {
        address: scanner.ipAddress,
        subnet_mask:
          PREFIX_TO_MASK_MAP[
            scanner.subnetMask as keyof typeof PREFIX_TO_MASK_MAP
          ] || scanner.subnetMask,
        gateway: scanner.gateway,
      },
  primary_dns: scanner.primaryDns,
  secondary_dns: scanner.secondaryDns,
  model: scanner.model,
  shipping_address: scanner.shippingSiteLocation,
  shipping_contact: {
    id: scanner.shippingContactId,
    first_name: scanner.shippingContactFirstName,
    last_name: scanner.shippingContactLastName,
    email: scanner.shippingContactEmail,
    phone: [
      {
        number: scanner.shippingContactPhoneNumber,
        type: scanner.shippingContactPhoneType,
      },
    ],
  },
  shipping_notes: scanner.shippingNotes,
  vanilla: scanner.vanilla,
  configuration_task_id: scanner.configurationTaskID,
});

const scannersToAPI = (
  scanners: OBScanners,
): { scanners: ReadonlyArray<RawOBScanner>; completed: boolean } => {
  return {
    scanners: [
      ...Object.entries(scanners.scanners).map(([id, scanner]) =>
        scannerToAPI(id, scanner),
      ),
    ],
    completed: scanners.completed,
  };
};

const scannersFromAPI = (rawScanners: {
  scanners: ReadonlyArray<RawOBScanner>;
  completed: boolean;
}): OBScanners => {
  return {
    scanners: rawScanners.scanners.reduce<OBIdToScannerMap>(
      (map, rawScanner) => ({
        ...map,
        [rawScanner.id]: {
          name: rawScanner.name,
          model: rawScanner.model,
          siteId: rawScanner.location_id,
          isIPAutoConfig: !rawScanner.network_configuration,
          ipAddress: rawScanner.network_configuration?.address ?? '',
          subnetMask: rawScanner.network_configuration?.subnet_mask ?? '',
          gateway: rawScanner.network_configuration?.gateway ?? '',
          primaryDns: rawScanner.primary_dns ?? '',
          secondaryDns: rawScanner.secondary_dns ?? '',
          isShippingToNewSite: !rawScanner.shipping_address.id,
          shippingSiteId: rawScanner.shipping_address.id,
          shippingSiteLocation: { ...rawScanner.shipping_address },
          isShippingToNewContact: !rawScanner.shipping_contact.id,
          shippingContactId: rawScanner.shipping_contact.id,
          shippingContactFirstName: rawScanner.shipping_contact.first_name,
          shippingContactLastName: rawScanner.shipping_contact.last_name,
          shippingContactEmail: rawScanner.shipping_contact.email,
          shippingContactPhoneNumber:
            rawScanner.shipping_contact.phone[0].number,
          shippingContactPhoneType: rawScanner.shipping_contact.phone[0].type,
          shippingNotes: rawScanner.shipping_notes,
          vanilla: !!rawScanner.vanilla,
          configurationTaskID: rawScanner.configuration_task_id,
        },
      }),
      {},
    ),
    completed: rawScanners.completed,
    loading: false,
  };
};

const virtualScannerFromAPI = (
  rawConfig: RawOBVirtualScanner,
): OBVirtualScanner => {
  return {
    software: rawConfig.software || OBVirtualizationSoftware.NONE,
    requirementsMet: rawConfig.acknowledged,
    noVirtualScanners: rawConfig.no_virtual_scanners,
    completed: rawConfig.completed,
  };
};

const virtualScannerToAPI = (config: OBVirtualScanner): RawOBVirtualScanner => {
  return {
    software: config.software,
    acknowledged: config.requirementsMet,
    no_virtual_scanners: config.noVirtualScanners,
    completed: config.completed,
  };
};

const availableEndpointsFromAPI = (
  rawAvailableEndpoints: RawOBAvailableEndpoints,
): OBAvailableEndpoints => {
  return {
    onPremiseEndpoints: rawAvailableEndpoints.on_premise_endpoints,
    cloudEndpoints: rawAvailableEndpoints.cloud_endpoints,
  };
};

const endpointConfigFromAPI = (
  rawConfig: RawOBEndpointConfig,
): OBEndpointConfig => {
  return {
    onPremEpps: rawConfig.on_prem_epps,
    cloudEpps: rawConfig.cloud_epps,
    notes: rawConfig.notes,
    completed: rawConfig.completed,
    noOnPremEpps: rawConfig.no_on_prem_epps,
    noCloudEpps: rawConfig.no_cloud_epps,
    noAgents: rawConfig.no_agents,
    sysmonInstalled: rawConfig.sysmon_installed,
    windowsEndpoints: rawConfig.windows_endpoints.toString(),
    macEndpoints: rawConfig.mac_endpoints.toString(),
    linuxEndpoints: rawConfig.linux_endpoints.toString(),
    agentCompleted: rawConfig.agent_completed,
    eppCompleted: rawConfig.epp_completed,
    finalized: rawConfig.finalized,
  };
};

const endpointConfigToAPI = (config: OBEndpointConfig): RawOBEndpointConfig => {
  return {
    on_prem_epps: config.onPremEpps,
    cloud_epps: config.cloudEpps,
    notes: config.notes,
    completed: config.completed,
    no_on_prem_epps: config.noOnPremEpps,
    no_cloud_epps: config.noCloudEpps,
    no_agents: config.noAgents,
    sysmon_installed: config.sysmonInstalled,
    windows_endpoints: parseInt(config.windowsEndpoints, 10),
    mac_endpoints: parseInt(config.macEndpoints, 10),
    linux_endpoints: parseInt(config.linuxEndpoints, 10),
    epp_completed: config.eppCompleted,
    agent_completed: config.agentCompleted,
    finalized: config.finalized,
  };
};

const availableVirtualSensorsFromAPI = (
  rawOBAvailableVirtualSensors: RawOBAvailableVirtualSensors,
): OBAvailableVirtualSensors => {
  return {
    sensors: rawOBAvailableVirtualSensors.sensors,
  };
};

const virtualSensorConfigToAPI = (
  config: OBVirtualSensorConfig,
): RawOBVirtualSensorConfig => {
  return {
    hypervisor: config.hypervisor,
    no_virtual_sensor: config.noVirtualSensor,
    virtualization_requirements: config.virtualizationRequirements,
    completed: config.completed,
  };
};

const virtualSensorConfigFromAPI = (
  config: RawOBVirtualSensorConfig,
): OBVirtualSensorConfig => {
  return {
    hypervisor: config.hypervisor,
    noVirtualSensor: config.no_virtual_sensor,
    virtualizationRequirements: config.virtualization_requirements,
    completed: config.completed,
  };
};

export type OBRawProducts = {
  services: string[] | null;
  hardware_quantity: { [key: string]: number } | null;
  cloud_sensors: string[] | null;
  virtual_sensors: string[] | null;
};

const productsFromAPI = (config: OBRawProducts): OBProducts => {
  return {
    services: config.services ?? [],
    hardwareQuantity: config.hardware_quantity ?? {},
    cloudSensors: config.cloud_sensors ?? [],
    virtualSensors: config.virtual_sensors ?? [],
  } as OBProducts;
};

const getHardwareInfoByHardwareKey = (
  hardware: OBHardwareInfo[],
  key: string,
): OBHardwareInfo | undefined => hardware.find((h) => h.hardware_key === key);

const getHardwareInfosByAllotmentKey = (
  hardware: OBHardwareInfo[],
  key: string,
): OBHardwareInfo[] => hardware.filter((h) => h.allotment_key === key);

/**
 * On followup phase data completion, describe how many addition items are
 * being submitted. (Site, Sensor, Scanner.)
 */
const getSubmissionSummary = (store: Store): string => {
  if (store.onboarding.sitesSection.finalized) {
    return '';
  }

  const newSites = Object.entries(store.onboarding.sitesSection.sites).filter(
    ([, site]) => !site.finalized,
  );

  let sensorCount = 0,
    scannerCount = 0;

  newSites.forEach(([siteId, site]) => {
    sensorCount += Object.values(
      store.onboarding.sensorsSection.sensors,
    ).filter((sensor) => sensor.siteId === siteId).length;
    scannerCount += Object.values(
      store.onboarding.scannersSection.scanners,
    ).filter((scanners) => scanners.siteId === siteId).length;
  });

  return [
    getPluralOrSingularNoun(newSites.length, 'Site'),
    getPluralOrSingularNoun(sensorCount, 'Sensor'),
    getPluralOrSingularNoun(scannerCount, 'Scanner'),
  ]
    .filter((s) => s)
    .join(', ');
};

const onboardingUtils = {
  contactToAPI,
  contactsToAPI,
  contactsFromAPI,
  contactFromAPI,
  internetExposureFromAPI,
  internetExposureToAPI,
  siteFromAPI,
  sitesFromAPI,
  siteToAPI,
  sitesToAPI,
  sensorsFromAPI,
  sensorsToAPI,
  scannersToAPI,
  scannersFromAPI,
  cloudSensorsFromAPI,
  cloudSensorsToAPI,
  virtualScannerFromAPI,
  virtualScannerToAPI,
  endpointConfigToAPI,
  virtualSensorConfigToAPI,
  virtualSensorConfigFromAPI,
  availableVirtualSensorsFromAPI,
  endpointConfigFromAPI,
  availableEndpointsFromAPI,
  productsFromAPI,
  getHardwareInfosByAllotmentKey,
  getHardwareInfoByHardwareKey,
  getSubmissionSummary,
};

export default onboardingUtils;
